import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  Trash,
  PencilSimple,
  FilePdf,
  Seal,
} from "@phosphor-icons/react";

const ConfirmationsPage = () => {
  const navigate = useNavigate();
  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/confirmations");
      setConfirmations(res.data);
    } catch {
      toast.error("Failed to load confirmations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = confirmations.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.guest_name || "").toLowerCase().includes(s) ||
           (c.hotel_name || "").toLowerCase().includes(s) ||
           (c.confirmation_ref || "").toLowerCase().includes(s) ||
           (c.tour_name || "").toLowerCase().includes(s) ||
           (c.pickup_location || "").toLowerCase().includes(s) ||
           (c.transfer_type || "").toLowerCase().includes(s);
  });

  const handleDelete = async () => {
    try {
      await api.delete(`/confirmations/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const downloadPDF = async (conf) => {
    try {
      const res = await api.get(`/confirmations/${conf.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Confirmation_${conf.hotel_name || "Hotel"}_${conf.guest_name || "Guest"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="confirmations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-1">Confirmations</p>
          <h1 className="font-serif text-3xl text-navy">Hotel Confirmation Letters</h1>
        </div>
        <Button onClick={() => navigate("/confirmations/new")} className="bg-navy hover:bg-navy/90 text-white gap-2" data-testid="new-confirmation-btn">
          <Plus size={16} /> New Confirmation
        </Button>
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by guest, hotel, or ref..." className="pl-9 bg-white border-border" />
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-border">
          <Seal size={40} className="mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground text-sm">No confirmation letters yet</p>
          <p className="text-muted-foreground/60 text-xs mt-1">Create one from scratch or from a proposal</p>
        </div>
      ) : (
        <div className="bg-white border border-border divide-y divide-border">
          {filtered.map((conf) => (
            <div key={conf.id} className="flex items-center justify-between p-4 hover:bg-cream/30 transition-colors" data-testid={`conf-item-${conf.id}`}>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/confirmations/${conf.id}/edit`)}>
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="font-serif text-base text-navy font-bold">
                    {conf.service_type === "transfer" ? (conf.transfer_type || "Transfer") :
                     conf.service_type === "tour" ? (conf.tour_name || "Tour") :
                     conf.service_type === "meet_and_greet" ? "Meet & Greet" :
                     (conf.hotel_name || "Untitled")}
                  </span>
                  {conf.service_type && conf.service_type !== "hotel" && (
                    <span className="text-[10px] bg-navy/10 text-navy px-2 py-0.5 uppercase tracking-wider">{conf.service_type.replace("_", " ")}</span>
                  )}
                  {conf.confirmation_ref && <span className="text-[10px] bg-gold/10 text-gold px-2 py-0.5 font-semibold">{conf.confirmation_ref}</span>}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  {conf.guest_name && <span>{conf.guest_name}</span>}
                  {conf.check_in && <span>{conf.check_in}</span>}
                  {conf.pickup_date && !conf.check_in && <span>{conf.pickup_date}</span>}
                  {conf.tour_date && <span>{conf.tour_date}</span>}
                  {conf.nights > 0 && <span>{conf.nights}N</span>}
                  {conf.room_type && <span>{conf.room_type}</span>}
                  {conf.pickup_location && <span>{conf.pickup_location}</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => downloadPDF(conf)} className="h-8 w-8 p-0 text-gold hover:text-gold/80" title="Download PDF">
                  <FilePdf size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/confirmations/${conf.id}/edit`)} className="h-8 w-8 p-0 text-muted-foreground hover:text-navy">
                  <PencilSimple size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteId(conf.id)} className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive">
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Confirmation?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this confirmation letter.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConfirmationsPage;
